import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { cn } from "../../lib/utils";

export default function AppModal({
  children,
  visible,
  overlayStyle,
  modalStyle,
  fadeDurationMs = 300,
}: {
  children: ReactNode;
  visible: boolean;
  overlayStyle?: CSSProperties;
  modalStyle?: CSSProperties;
  fadeDurationMs?: number;
}) {
  const [status, setStatus] = useState<
    "open" | "opening" | "closing" | "closed"
  >("closed");
  const isVisible = status !== "closed";

  useEffect(() => {
    if (visible && status !== "open") {
      setStatus("opening");
      const openTimer = setTimeout(() => {
        setStatus("open");
      }, fadeDurationMs);
      return () => {
        clearTimeout(openTimer);
      };
    }
    if (!visible && status !== "closed") {
      setStatus("closing");
      const closeTimer = setTimeout(() => {
        setStatus("closed");
      }, fadeDurationMs);
      return () => {
        clearTimeout(closeTimer);
      };
    }
    // Already settled open or closed: no fade is in flight, so there is
    // nothing to tear down.
    return;
  }, [visible]);

  return createPortal(
    <div
      className={cn(
        "box-border opacity-0 fixed top-0 left-0 w-full h-dvh z-999 md:left-1/2 md:-translate-x-1/2 md:w-[600px]",
        isVisible ? "block" : "hidden"
      )}
      style={{
        animation:
          status === "open" || status === "opening"
            ? `fadeIn ${fadeDurationMs}ms ease-in-out forwards`
            : `fadeOut ${fadeDurationMs}ms ease-in-out forwards`,
      }}
    >
      <div className="size-[inherit] bg-[#000c]" style={overlayStyle} />

      <RemoveScroll enabled={isVisible}>
        <div
          className="box-border fixed top-1/2 left-1/2 z-1000 -translate-1/2 w-full max-w-[calc(100dvw-40px)] p-6 rounded-2xl flex flex-col gap-6 bg-primary-yellow text-primary-black md:max-w-140"
          style={modalStyle}
        >
          {children}
        </div>
      </RemoveScroll>
    </div>,
    document.body
  );
}
